import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppProviders } from '@/providers/AppProviders';
import { useAuth } from '@/providers/auth-context';
import { AppShell } from '@/components/layout/AppShell';
import { PageSpinner } from '@/components/spinner';
import { LoginPage } from '@/pages/Login';

const HomePage = lazy(() => import('@/pages/Home').then((m) => ({ default: m.HomePage })));
const CompaniesListPage = lazy(() => import('@/pages/companies/CompaniesList'));
const UsersListPage = lazy(() => import('@/pages/users/UsersList'));
const CompanyLayout = lazy(() => import('@/pages/companies/CompanyLayout'));
const CompanySettings = lazy(() => import('@/pages/companies/CompanySettings'));
const CompanyUsers = lazy(() => import('@/pages/companies/CompanyUsers'));
const CompanyInvites = lazy(() => import('@/pages/companies/CompanyInvites'));
const CompanyPlan = lazy(() => import('@/pages/companies/CompanyPlan'));
const CompanyFeatures = lazy(() => import('@/pages/companies/CompanyFeatures'));
const CompanyActivity = lazy(() => import('@/pages/companies/CompanyActivity'));
const CompanyCredits = lazy(() => import('@/pages/companies/CompanyCredits'));
const CompanyRelations = lazy(() => import('@/pages/companies/CompanyRelations'));
const CompanyFinancials = lazy(() => import('@/pages/companies/CompanyFinancials'));
const CompanyContracts = lazy(() => import('@/pages/companies/CompanyContracts'));
const CompanyContractNew = lazy(() => import('@/pages/companies/CompanyContractNew'));
const CompanyNew = lazy(() => import('@/pages/companies/CompanyNew'));
const RegionsLayout = lazy(() => import('@/pages/regions/RegionsLayout'));
const RegionsCountries = lazy(() => import('@/pages/regions/Countries'));
const RegionsGroups = lazy(() => import('@/pages/regions/Groups'));
const RegionsTiers = lazy(() => import('@/pages/regions/Tiers'));
const FeaturedSongs = lazy(() => import('@/pages/songs/FeaturedSongs'));
const FlaggedSongs = lazy(() => import('@/pages/songs/FlaggedSongs'));
const CreatorsList = lazy(() => import('@/pages/creators/CreatorsList'));
const CreatorDetail = lazy(() => import('@/pages/creators/CreatorDetail'));
const CreatorDuplicates = lazy(() => import('@/pages/creators/Duplicates'));
const AiSongs = lazy(() => import('@/pages/songs/AiSongs'));
const CataloguesLayout = lazy(() => import('@/pages/catalogues/CataloguesLayout'));
const CataloguesOverview = lazy(() => import('@/pages/catalogues/Overview'));
const CataloguesList = lazy(() => import('@/pages/catalogues/CataloguesList'));
const AccountManagers = lazy(() => import('@/pages/catalogues/AccountManagers'));
const ImportsLayout = lazy(() => import('@/pages/imports/ImportsLayout'));
const ImportQueue = lazy(() => import('@/pages/imports/ImportQueue'));
const NewImport = lazy(() => import('@/pages/imports/NewImport'));
const PromoCodes = lazy(() => import('@/pages/promo-codes/PromoCodes'));
const SystemAnalytics = lazy(() => import('@/pages/analytics/SystemAnalytics'));
const SearchStats = lazy(() => import('@/pages/search-stats/SearchStats'));
const SearchStatsDrill = lazy(() => import('@/pages/search-stats/SearchStatsDrill'));
const CreditPackages = lazy(() => import('@/pages/credit-packages/CreditPackages'));
const TiersLayout = lazy(() => import('@/pages/tiers/TiersLayout'));
const Tiers = lazy(() => import('@/pages/tiers/Tiers'));
const TierOffersAndDeals = lazy(() => import('@/pages/tiers/OffersAndDeals'));
const TierCompare = lazy(() => import('@/pages/tiers/Compare'));
const TierPlanPage = lazy(() => import('@/pages/tiers/PlanPage'));
const TierDefaults = lazy(() => import('@/pages/tiers/Defaults'));
const TierReporting = lazy(() => import('@/pages/tiers/Reporting'));
const TierActivity = lazy(() => import('@/pages/tiers/Activity'));
const TierChanges = lazy(() => import('@/pages/tiers/Changes'));
const OfferRedemptions = lazy(() => import('@/pages/signup-offers/OfferRedemptions'));
const VatLayout = lazy(() => import('@/pages/vat/VatLayout'));
const VatMissing = lazy(() => import('@/pages/vat/MissingVat'));
const VatAuditLog = lazy(() => import('@/pages/vat/AuditLog'));
const VatReports = lazy(() => import('@/pages/vat/Reports'));
const CreditsLayout = lazy(() => import('@/pages/credits/CreditsLayout'));
const CreditsOverview = lazy(() => import('@/pages/credits/Overview'));
const CreditsLedger = lazy(() => import('@/pages/credits/Ledger'));
const FinancialsAdmin = lazy(() => import('@/pages/financials/FinancialsAdmin'));
const ContactMessages = lazy(() => import('@/pages/contact-messages/ContactMessages'));
const ContactMessageDetail = lazy(() => import('@/pages/contact-messages/ContactMessageDetail'));
const NotFoundPage = lazy(() =>
  import('@/pages/NotFound').then((m) => ({ default: m.NotFoundPage })),
);

function AuthGate() {
  const { status } = useAuth();

  if (status === 'loading') {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center">
        <PageSpinner />
      </div>
    );
  }

  if (status !== 'authenticated') {
    return <LoginPage />;
  }

  return (
    <AppShell>
      <Suspense fallback={<PageSpinner />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/users" element={<UsersListPage />} />
          <Route path="/companies" element={<CompaniesListPage />} />
          <Route path="/companies/new" element={<CompanyNew />} />
          <Route path="/companies/:id" element={<CompanyLayout />}>
            <Route index element={<CompanySettings />} />
            <Route path="users" element={<CompanyUsers />} />
            <Route path="invites" element={<CompanyInvites />} />
            <Route path="plan" element={<CompanyPlan />} />
            <Route path="features" element={<CompanyFeatures />} />
            <Route path="activity" element={<CompanyActivity />} />
            <Route path="credits" element={<CompanyCredits />} />
            <Route path="relations" element={<CompanyRelations />} />
            <Route path="financials" element={<CompanyFinancials />} />
            <Route path="contracts" element={<CompanyContracts />} />
            <Route path="contracts/new" element={<CompanyContractNew />} />
          </Route>
          <Route path="/regions" element={<RegionsLayout />}>
            <Route index element={<RegionsCountries />} />
            <Route path="groups" element={<RegionsGroups />} />
            <Route path="tiers" element={<RegionsTiers />} />
          </Route>
          <Route path="/catalogues" element={<CataloguesLayout />}>
            <Route index element={<CataloguesOverview />} />
            <Route path="list" element={<CataloguesList />} />
            <Route path="managers" element={<AccountManagers />} />
          </Route>
          <Route path="/featured-songs" element={<FeaturedSongs />} />
          <Route path="/flagged-songs" element={<FlaggedSongs />} />
          <Route path="/creators" element={<CreatorsList />} />
          <Route path="/creators/duplicates" element={<CreatorDuplicates />} />
          <Route path="/creators/:id" element={<CreatorDetail />} />
          <Route path="/ai-songs" element={<AiSongs />} />
          <Route path="/imports" element={<ImportsLayout />}>
            <Route index element={<ImportQueue />} />
            <Route path="new" element={<NewImport />} />
          </Route>
          <Route path="/promo-codes" element={<PromoCodes />} />
          <Route path="/analytics" element={<SystemAnalytics />} />
          <Route path="/search-stats" element={<SearchStats />} />
          <Route path="/search-stats/:view" element={<SearchStats />} />
          <Route path="/search-stats/:view/:outerTab" element={<SearchStats />} />
          <Route path="/search-stats/:view/top-searches/:innerTab" element={<SearchStats />} />
          <Route
            path="/search-stats/:view/top-searches/:innerTab/q/:q"
            element={<SearchStatsDrill />}
          />
          <Route
            path="/search-stats/:view/top-searches/:innerTab/f/:filterKeys"
            element={<SearchStatsDrill />}
          />
          <Route path="/search-stats/:view/:outerTab/q/:q" element={<SearchStatsDrill />} />
          <Route
            path="/search-stats/:view/:outerTab/f/:filterKeys"
            element={<SearchStatsDrill />}
          />
          <Route path="/search-stats/:view/event/:eventId" element={<SearchStatsDrill />} />
          <Route path="/credit-packages" element={<CreditPackages />} />
          <Route path="/tiers" element={<TiersLayout />}>
            <Route index element={<Tiers />} />
            <Route path="offers" element={<TierOffersAndDeals />} />
            <Route path="private-deals" element={<Navigate to="/tiers/offers#private-deals" replace />} />
            <Route path="signup-offers" element={<Navigate to="/tiers/offers#signup-offers" replace />} />
            <Route path="signup-offers/:id" element={<OfferRedemptions />} />
            <Route path="plans/:tierName" element={<TierPlanPage />} />
            <Route path="compare" element={<TierCompare />} />
            <Route path="limits" element={<Navigate to="/tiers/compare" replace />} />
            <Route path="defaults" element={<TierDefaults />} />
            <Route path="features" element={<Navigate to="/tiers/defaults#features" replace />} />
            <Route path="seat-classes" element={<Navigate to="/tiers/defaults#seat-classes" replace />} />
            <Route path="reporting" element={<TierReporting />} />
            <Route path="activity" element={<TierActivity />} />
            <Route path="changes" element={<TierChanges />} />
          </Route>
          <Route path="/vat" element={<VatLayout />}>
            <Route index element={<VatMissing />} />
            <Route path="audit-log" element={<VatAuditLog />} />
            <Route path="reports" element={<VatReports />} />
          </Route>
          <Route path="/features" element={<Navigate to="/tiers/defaults#features" replace />} />
          <Route path="/credits" element={<CreditsLayout />}>
            <Route index element={<CreditsOverview />} />
            <Route path="ledger" element={<CreditsLedger />} />
          </Route>
          <Route path="/financials" element={<FinancialsAdmin />} />
          <Route path="/contact-messages" element={<ContactMessages />} />
          <Route path="/contact-messages/:id" element={<ContactMessageDetail />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </AppShell>
  );
}

export function App() {
  return (
    <AppProviders>
      <AuthGate />
    </AppProviders>
  );
}
